// CategoryNavigation.jsx
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import api from "../Api.js";
import './CategoryNavigation.css';

// Fallback image for categories without icon
const FALLBACK_IMAGE = 'https://png.pngtree.com/png-vector/20221125/ourmid/pngtree-no-image-available-icon-flatvector-illustration-picture-coming-creative-vector-png-image_40968940.jpg';

const getRelatedId = (value) => (
  typeof value === 'string' ? value : value?._id
);

const CategoryNavigation = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [subChildCategories, setSubChildCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const sectionRef = React.useRef(null);

  // Expose scrollToSection method to parent component
  useImperativeHandle(ref, () => ({
    scrollToSection: () => {
      if (sectionRef.current) {
        const offset = 6; // Adjust based on your header height
        const elementPosition = sectionRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Add highlight effect
        sectionRef.current.classList.add('highlight-pulse');
        setTimeout(() => {
          if (sectionRef.current) {
            sectionRef.current.classList.remove('highlight-pulse');
          }
        }, 2000);
      }
    }
  }));

  // Fetch all data on component mount
  useEffect(() => {
    fetchAllCategoryData();
  }, []);

  const fetchAllCategoryData = async () => {
    try {
      setLoading(true);
      
      const [
        categoriesRes,
        subCategoriesRes,
        childCategoriesRes,
        subChildCategoriesRes
      ] = await Promise.all([
        api.get('/api/web/categories'),
        api.get('/api/web/sub-categories'),
        api.get('/api/web/child-categories'),
        api.get('/api/web/sub-child-categories')
      ]);

      const activeCategories = categoriesRes.data.data.filter(cat => cat.status === true);
      setCategories(activeCategories);
      
      const activeSubCategories = subCategoriesRes.data.data.filter(sub => sub.status === true);
      setSubCategories(activeSubCategories);
      
      const activeChildCategories = childCategoriesRes.data.data.filter(child => child.status === true);
      setChildCategories(activeChildCategories);
      
      const activeSubChildCategories = subChildCategoriesRes.data.data.filter(subChild => subChild.status === true);
      setSubChildCategories(activeSubChildCategories);

      setError(null);
    } catch (err) {
      console.error('Error fetching category data:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get sub-categories for a specific category
  const getSubCategoriesByCategory = (categoryId) => {
    return subCategories.filter(sub => getRelatedId(sub.categoryId) === categoryId);
  };

  // Get child categories for a specific sub-category
  const getChildCategoriesBySubCategory = (subCategoryId) => {
    return childCategories.filter(child => getRelatedId(child.subCategoryId) === subCategoryId);
  };

  // Get sub-child categories for a specific child category
  const getSubChildCategoriesByChild = (childCategoryId) => {
    return subChildCategories.filter(subChild => getRelatedId(subChild.childCategoryId) === childCategoryId);
  };

  // Handle category click - open modal
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);
    setIsModalOpen(true);
  };

  // Handle sub-category click - navigate to category page
  const handleSubCategoryClick = (subCategory) => {
    setSelectedSubCategory(subCategory);
    // Navigate to category page with subcategory data
    navigate(`/category/${selectedCategory._id}`, {
      state: {
        category: selectedCategory,
        subCategory: subCategory,
        childCategories: getChildCategoriesBySubCategory(subCategory._id),
        subChildCategories: subChildCategories
      }
    });
    setIsModalOpen(false);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
  };

  if (loading) {
    return (
      <div className="category-loader">
        <div className="loader-spinner"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  if (error) {
    return <div className="category-error">{error}</div>;
  }

  return (
    <div className="category-navigation" id="categories" ref={sectionRef}>
      <div className="categories-container">
        <div className="category-header">
          <h2>What can we help you with?</h2>
          <p>Choose a service category to find the right local professional</p>
        </div>
        
        <div className="categories-grid">
          {categories.map((category) => (
            <div
              key={category._id}
              className="category-card"
              onClick={() => handleCategoryClick(category)}
            >
              <div className="category-card-image">
                <img 
                  src={category.icon || FALLBACK_IMAGE} 
                  alt={category.categoryName}
                  onError={(e) => {
                    e.target.src = FALLBACK_IMAGE;
                  }}
                />
              </div>
              <div className="category-card-content">
                <h3>{category.categoryName}</h3>
                {category.description && (
                  <p>{category.description}</p>
                )}
                <span className="category-card-arrow">→</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal for Sub-Categories */}
      {isModalOpen && selectedCategory && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedCategory.categoryName}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            <div className="modal-body">
              <p className="modal-subtitle">Select a sub-category</p>
              <div className="subcategories-grid">
                {getSubCategoriesByCategory(selectedCategory._id).length > 0 ? (
                  getSubCategoriesByCategory(selectedCategory._id).map((sub) => (
                    <div
                      key={sub._id}
                      className="subcategory-card"
                      onClick={() => handleSubCategoryClick(sub)}
                    >
                      <div className="subcategory-icon">
                        <span>📋</span>
                      </div>
                      <div className="subcategory-info">
                        <h4>{sub.subCategoryName}</h4>
                        <span className="subcategory-count">
                          {getChildCategoriesBySubCategory(sub._id).length} services
                        </span>
                      </div>
                      <span className="subcategory-arrow">→</span>
                    </div>
                  ))
                ) : (
                  <p className="no-subcategories">No sub-categories available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default CategoryNavigation;